import React from 'react';

import { IconArrowLeft } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Typography,
} from '@cfa/react-components';

import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { itemByLanguage } from '@/utils/language';

import QuizPreviewCard from '@/components/Cards/QuizPreviewCard';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';

import Constants from '@/constants/index';

import { getCustomQuizDetails1Options } from '@/_generated/api-clients/content';
import { CustomQuizDto, Plan } from '@/_generated/api-clients/xp';

interface QuizPreviewPopUpProps {
  handleQuizBackButton: () => void;
  isOpen: boolean;
  onClose: () => void;
  quizToPreview: CustomQuizDto;
  plan?: Plan;
  sectionId: string;
}

const QuizPreviewPopUp: React.FC<QuizPreviewPopUpProps> = ({
  handleQuizBackButton,
  isOpen,
  onClose,
  quizToPreview,
  plan,
  sectionId,
}) => {
  const { t } = useTranslation();
  const { addSteps } = useUpdatePlan({
    onSuccess: () => {
      onClose();
    },
  });

  const {
    data: quiz,
    error: quizError,
    isFetching: isFetchingQuiz,
  } = useQuery({
    ...getCustomQuizDetails1Options({
      path: { customQuizId: quizToPreview.id ?? '' },
    }),
    enabled: !!quizToPreview.id,
  });

  const withinSection = !!sectionId.length;

  const quizName = quizToPreview.name ? itemByLanguage(quizToPreview.name) : '';

  const getQuizStep = () => ({
    id: uuidv4(),
    name: {
      en: itemByLanguage(quizToPreview.name, '', 'en'),
      es: itemByLanguage(quizToPreview.name, '', 'es'),
    },
    type: Constants.STEP_TYPES.QUIZ,
    quizId: quizToPreview.typeFormId,
    reference: undefined,
    note: undefined,
    urls: [],
    available: true,
  });

  const onAddQuizFromPreview = async () => {
    if (!plan) return;
    const quizStep = getQuizStep();
    await addSteps(plan, [quizStep], withinSection ? sectionId : undefined);
  };

  return (
    <>
      <LoadingOverlay isOpen={isFetchingQuiz} />
      <Modal
        onClose={onClose}
        scrollMode="modal-body"
        show={isOpen}
        size="lg"
        className="[&_.cfa-modal-header]:pb-8!"
      >
        <ModalHeader className="flex! justify-center!">
          <div className="flex w-full items-center">
            <div className="flex flex-1 whitespace-nowrap">
              <Button
                color="secondary"
                onClick={handleQuizBackButton}
                size="sm"
                variant="text"
                className="-translate-x-2.5! pl-0!"
              >
                <IconArrowLeft className="pr-1!" />
              </Button>
            </div>
            <div>
              <Typography variant="overline1" className="mb-2!">
                {t('TrainingPlans.preview')}
              </Typography>
              {quizName}
            </div>
            <div className="min-w-[155px] flex-1" />
          </div>
        </ModalHeader>
        <ModalBody>
          {!isFetchingQuiz && (
            <QuizPreviewCard quiz={quiz} quizError={quizError} />
          )}
        </ModalBody>
        <ModalFooter className="py-4!">
          <Button
            color="secondary"
            data-testid="addQuizFromPreviewButton"
            onClick={onAddQuizFromPreview}
            variant="filled"
            className="min-w-0!"
          >
            {t('Button.add')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default QuizPreviewPopUp;
